import { ILogsFilters } from "../models/filters";
import { ITaskLogs, ICalculation, ISavedCalculation, ISubtaskLogs } from "../models/calculation";
import { ITask } from "../models/task";
import { ISubtask } from "../models/subtask";
import { IPlotVertices } from "../models/vertexPlot";
import { IConsoleMessage } from "./consoleHubService";
import { getAuthorizationProperties } from "./authorizationHelper";
import { IWorkerInfo } from "../store/appInfoReducer";
import { toast } from "..";
import { LtccType, ValidationProcess } from "../store/validationProcess";

export async function getCalculations(
    page: number,
    pageSize: number,
    logsFilters: ILogsFilters,
    validationProcess: ValidationProcess | null
): Promise<ITaskLogs> {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("pageSize", pageSize.toString());
    if (logsFilters !== undefined) {
        if (logsFilters.calculationDate)
            params.append("calculationDate", logsFilters.calculationDate);
        if (logsFilters.user) params.append("user", logsFilters.user);
        if (logsFilters.started) params.append("started", logsFilters.started);
        if (logsFilters.action > -1) params.append("calculationType", logsFilters.action + "");
        params.append("calculationStatus", logsFilters.calculationStatus.toString());
    }
    if (validationProcess !== null) {
        params.append("validationProcess", validationProcess.toString());
    }

    return fetch("/api/calculations?" + params, {
        method: "GET",
        ...getAuthorizationProperties({ "Content-Type": "application/json" }),
    }).then((res: Response) => res.json());
}

export async function getCalculation(guid: string): Promise<ITask> {
    return fetch(`/api/calculations/${guid}`, {
        method: "GET",
        ...getAuthorizationProperties({ "Content-Type": "application/json" }),
    }).then((res: Response) => res.json());
}

export async function getCalculationInputFiles(guid: string): Promise<string[]> {
    return fetch(`/api/calculations/${guid}/files`, {
        method: "GET",
        ...getAuthorizationProperties({ "Content-Type": "application/json" }),
    }).then((res: Response) => {
        if (res.ok) return res.json();
        else return [];
    });
}

export async function checkSavedCalculation(
    userGuid: string,
    validationProcess: ValidationProcess
): Promise<Response> {
    return fetch(
        `/api/calculations/checksave?userGuid=${userGuid}&validationProcess=${validationProcess}`,
        {
            method: "GET",
            ...getAuthorizationProperties({ "Content-Type": "application/json" }),
        }
    );
}

export async function saveCalculation(
    savedCalculation: ISavedCalculation,
    validationProcess: ValidationProcess
): Promise<Response> {
    return fetch(`/api/calculations/save?validationProcess=${validationProcess}`, {
        method: "PUT",
        body: JSON.stringify(savedCalculation),
        ...getAuthorizationProperties({ "Content-Type": "application/json;charset=UTF-8" }),
    });
}

export async function loadSavedCalculation(
    userGuid: string,
    validationProcess: ValidationProcess
): Promise<Response> {
    return fetch(
        `/api/calculations/load?userGuid=${userGuid}&validationProcess=${validationProcess}`,
        {
            method: "GET",
            ...getAuthorizationProperties({ "Content-Type": "application/json" }),
        }
    );
}

export async function downloadF310(
    guid: string,
    version: number,
    justification: string,
    validationProcess: ValidationProcess,
    ltccType: LtccType
) {
    const params = new URLSearchParams();
    params.append("version", version.toString());
    params.append("justification", justification);
    params.append("validationProcess", validationProcess.toString());
    params.append("ltccType", ltccType.toString());
    const res = await fetch(`/api/calculations/${guid}/downloadF310?` + params, {
        method: "GET",
        ...getAuthorizationProperties({ "Content-Type": "application/document" }),
    });
    if (!res.ok) {
        if (res.status === 401) {
            const xmlJustification = await res.text();
            toast(
                `F310 justification cannot be changed from ${xmlJustification}, a new run is required for a new justification`,
                false
            );
        }
        return false;
    }
    await handleDownload(res, "application/document");
    return true;
}

export async function downloadF427(guid: string, version: number) {
    const params = new URLSearchParams();
    params.append("version", version.toString());
    const res = await fetch(`/api/calculations/${guid}/downloadF427?` + params, {
        method: "GET",
        ...getAuthorizationProperties({ "Content-Type": "application/document" }),
    });
    if (!res.ok) return false;
    await handleDownload(res, "application/document");
    return true;
}

export async function downloadRaoResults(guid: string, calculationId: number) {
    const res = await fetch(
        `/api/calculations/${guid}/downloadRao?calculationId=${calculationId}`,
        {
            method: "GET",
            ...getAuthorizationProperties({ "Content-Type": "application/zip" }),
        }
    );
    if (!res.ok) return false;
    await handleDownload(res, "application/zip");
    return true;
}

export async function downloadRaoResultsForHour(guid: string, hour: number) {
    const res = await fetch(`/api/calculations/${guid}/downloadRao/${hour}`, {
        method: "GET",
        ...getAuthorizationProperties({ "Content-Type": "application/zip" }),
    });
    if (!res.ok) return false;
    await handleDownload(res, "application/zip");
    return true;
}

export async function downloadInputs(guid: string) {
    const res = await fetch(`/api/calculations/${guid}/downloadInputs`, {
        method: "GET",
        ...getAuthorizationProperties({ "Content-Type": "application/zip" }),
    });
    if (!res.ok) return false;
    await handleDownload(res, "application/zip");
    return true;
}

export async function downloadReports(
    guid: string,
    calculationId: number,
    calculationDate: string | undefined
) {
    const res = await fetch(
        `/api/calculations/${guid}/downloadReports?calculationId=${calculationId}&calculationDate=${calculationDate}`,
        {
            method: "GET",
            ...getAuthorizationProperties({ "Content-Type": "application/zip" }),
        }
    );
    if (!res.ok) return false;
    await handleDownload(res, "application/zip");
    return true;
}

export async function downloadGromInputs(guid: string, calculationId: number) {
    const res = await fetch(
        `/api/calculations/${guid}/downloadGromInputs?calculationId=${calculationId}`,
        {
            method: "GET",
            ...getAuthorizationProperties({ "Content-Type": "application/zip" }),
        }
    );
    if (!res.ok) return false;
    await handleDownload(res, "application/zip");
    return true;
}

export async function downloadGromOutputs(guid: string, calculationId: number) {
    const res = await fetch(
        `/api/calculations/${guid}/downloadGromOutputs?calculationId=${calculationId}`,
        {
            method: "GET",
            ...getAuthorizationProperties({ "Content-Type": "application/zip" }),
        }
    );
    if (!res.ok) return false;
    await handleDownload(res, "application/zip");
    return true;
}

export async function createCalculation(
    calculation: ICalculation,
    file: File | null
): Promise<Response> {
    const formData = new FormData();
    formData.append("calculation", JSON.stringify(calculation));
    if (file) formData.append("file", file, file.name);
    return fetch("/api/calculations", {
        method: "POST",
        ...getAuthorizationProperties({}),
        body: formData,
    });
}

export async function deleteCalculation(guid: string): Promise<Response> {
    return fetch(`/api/calculations/${guid}`, {
        method: "DELETE",
        ...getAuthorizationProperties({ "Content-Type": "application/json" }),
    });
}

export async function stopCalculation(guid: string): Promise<Response> {
    return fetch(`/api/calculations/stop/${guid}`, {
        method: "PATCH",
        ...getAuthorizationProperties({}),
    });
}

export async function getTasks(guid: string): Promise<ITask[]> {
    return fetch(`/api/calculations/${guid}/tasks`, {
        method: "GET",
        ...getAuthorizationProperties({ "Content-Type": "application/json" }),
    }).then((res: Response) => res.json());
}

export async function getSubtasks(guid: string, stage: string): Promise<ISubtaskLogs> {
    return fetch(`/api/calculations/${guid}/subtasks?stage=${stage}`, {
        method: "GET",
        ...getAuthorizationProperties({ "Content-Type": "application/json" }),
    }).then((res: Response) => res.json());
}

export async function getQueuedSubtasks(): Promise<ISubtaskLogs> {
    return fetch(`/api/calculations/subtasksQueue`, {
        method: "GET",
        ...getAuthorizationProperties({ "Content-Type": "application/json" }),
    }).then((res: Response) => res.json());
}

export async function getSubtask(guid: string): Promise<ISubtask> {
    return fetch(`/api/calculations/subtasks/${guid}`, {
        method: "GET",
        ...getAuthorizationProperties({ "Content-Type": "application/json" }),
    }).then((res: Response) => res.json());
}

export async function getSubtaskConsoleMessages(subtaskGuid: string): Promise<IConsoleMessage[]> {
    return fetch(`/api/calculations/subtasks/${subtaskGuid}/console`, {
        method: "GET",
        ...getAuthorizationProperties({ "Content-Type": "application/json" }),
    }).then((res: Response) => res.json());
}

export async function getChartVertices(parentGuid: string, hour: string): Promise<IPlotVertices> {
    return fetch(`/api/calculations/subtasks/${parentGuid}/chart?hour=${hour}`, {
        method: "GET",
        ...getAuthorizationProperties({ "Content-Type": "application/json" }),
    }).then((res: Response) => res.json());
}

export async function getWorkers(): Promise<IWorkerInfo[]> {
    return fetch(`/api/calculations/workers`, {
        method: "GET",
        ...getAuthorizationProperties({ "Content-Type": "application/json" }),
    }).then((res: Response) => res.json());
}

const handleDownload = async (res: Response, contentType: string) => {
    const blob = await res.blob();
    const contentDispositionHeader = res.headers.get("content-disposition");
    if (contentDispositionHeader) {
        const fileName = contentDispositionHeader.split("; ")[1].split("=")[1];
        const url = window.URL.createObjectURL(new Blob([blob], { type: contentType }));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode!.removeChild(link);
    } else return Promise.reject();
};
